import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Instagram, Send, Loader2 } from "lucide-react";
import { Reveal } from "./shared";
import { CONTACT, PROGRAMS } from "../../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", phone: "", email: "", program: "", message: "", website: "" };

export const Contact = ({ prefill = "" }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, program: prefill }));
  }, [prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Message sent! Our team replies within one business day.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section alt" id="contact" data-testid="contact-section">
      <div className="container">
        <Reveal><p className="eyebrow">Contact</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="section-title" style={{ marginTop: 16 }}>Start your <span className="accent">journey</span></h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="section-sub">Book your free trial class or send us an inquiry — our team replies within one business day.</p>
        </Reveal>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 50, marginTop: 48 }} className="contact-grid">
          <Reveal>
            <div style={{ background: "var(--red)", color: "#fff", borderRadius: "var(--radius-lg)", padding: 42,
              position: "relative", overflow: "hidden", height: "100%" }}>
              <div style={{ position: "absolute", bottom: -60, left: -60, width: 220, height: 220,
                background: "radial-gradient(circle,rgba(242,169,59,0.28),transparent 70%)" }} />
              {[
                { icon: <Phone size={20} />, main: CONTACT.phone, sub: "Call or WhatsApp", href: `https://wa.me/${CONTACT.phoneRaw}` },
                { icon: <Mail size={20} />, main: CONTACT.email, sub: "Email us anytime", href: `mailto:${CONTACT.email}` },
                { icon: <MapPin size={20} />, main: CONTACT.addressLine, sub: "Visit our dojo", href: "#map" },
              ].map((r, i) => (
                <a key={i} href={r.href} data-testid={`contact-info-${i}`}
                  style={{ display: "flex", gap: 16, alignItems: "center", padding: "16px 0",
                    borderBottom: i < 2 ? "1px solid rgba(255,255,255,0.15)" : "none", position: "relative", zIndex: 1 }}>
                  <span style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(255,255,255,0.14)",
                    display: "flex", alignItems: "center", justifyContent: "center", flex: "none", color: "var(--gold)" }}>{r.icon}</span>
                  <span>
                    <strong style={{ display: "block", fontSize: 14.5 }}>{r.main}</strong>
                    <span style={{ fontSize: 13, color: "rgba(255,255,255,0.78)" }}>{r.sub}</span>
                  </span>
                </a>
              ))}
              <div style={{ display: "flex", gap: 14, marginTop: 26, position: "relative", zIndex: 1 }}>
                <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram"
                  style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(255,255,255,0.14)",
                    display: "flex", alignItems: "center", justifyContent: "center" }}><Instagram size={20} /></a>
                <a href={`https://wa.me/${CONTACT.phoneRaw}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid="social-whatsapp"
                  style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(255,255,255,0.14)",
                    display: "flex", alignItems: "center", justifyContent: "center" }}><Phone size={20} /></a>
              </div>
              <div id="map" style={{ marginTop: 24, borderRadius: "var(--radius-md)", overflow: "hidden",
                height: 190, position: "relative", zIndex: 1, border: "1px solid rgba(255,255,255,0.15)" }}>
                <iframe title="VLA Location" src={CONTACT.mapEmbed} width="100%" height="100%"
                  style={{ border: 0, filter: "grayscale(0.2)" }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <form onSubmit={submit} data-testid="contact-form" noValidate
              style={{ background: "#fff", borderRadius: "var(--radius-lg)", padding: 42,
                boxShadow: "var(--shadow-soft)", border: "1px solid rgba(17,17,17,0.06)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }} className="form-row">
                <div className="field" style={{ marginBottom: 18 }}>
                  <label htmlFor="fname">Full Name</label>
                  <input id="fname" data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Your name" required />
                </div>
                <div className="field" style={{ marginBottom: 18 }}>
                  <label htmlFor="fphone">Phone</label>
                  <input id="fphone" data-testid="input-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 00000 00000" required />
                </div>
              </div>
              <div className="field" style={{ marginBottom: 18 }}>
                <label htmlFor="femail">Email</label>
                <input id="femail" data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" required />
              </div>
              <div className="field" style={{ marginBottom: 18 }}>
                <label htmlFor="fprog">Program Interested In</label>
                <select id="fprog" data-testid="select-program" value={form.program} onChange={set("program")}>
                  <option value="">Select a program</option>
                  {PROGRAMS.map((p) => <option key={p.title} value={p.title}>{p.title}</option>)}
                  <option value="Kids Program">Kids Program</option>
                  <option value="Not sure yet">Not sure yet</option>
                  {form.program && !["", "Boxing", "Kickboxing", "Taekwondo", "Self Defence", "Functional Fitness", "Weight Loss", "Kids Program", "Not sure yet"].includes(form.program) && (
                    <option value={form.program}>{form.program}</option>
                  )}
                </select>
              </div>
              <div className="field" style={{ marginBottom: 18 }}>
                <label htmlFor="fmsg">Message</label>
                <textarea id="fmsg" data-testid="input-message" value={form.message} onChange={set("message")} placeholder="Tell us a bit about your goals..." />
              </div>
              <button type="submit" className="btn btn-gold" data-testid="contact-submit-btn"
                disabled={loading} style={{ width: "100%", justifyContent: "center", opacity: loading ? 0.8 : 1 }}>
                {loading ? <><Loader2 size={17} className="spin" /> Sending…</> : <>Send Inquiry <Send size={16} /></>}
              </button>
              {/* Honeypot — must stay empty; hidden from real users */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px",
                width: 1, height: 1, overflow: "hidden" }}>
                <label htmlFor="website">Website</label>
                <input id="website" name="website" type="text" tabIndex="-1" autoComplete="off"
                  value={form.website} onChange={set("website")} data-testid="honeypot-website" />
              </div>
              <p style={{ marginTop: 14, fontSize: 12.5, color: "var(--muted)", textAlign: "center", lineHeight: 1.55 }}
                data-testid="contact-consent">
                By submitting this form, you agree to our{" "}
                <a href="/privacy-policy" style={{ color: "var(--red)", textDecoration: "underline" }} data-testid="consent-privacy-link">Privacy Policy</a>
                {" "}and{" "}
                <a href="/terms-and-conditions" style={{ color: "var(--red)", textDecoration: "underline" }} data-testid="consent-terms-link">Terms &amp; Conditions</a>.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
